/**
 *
 * @alias components.plugin.BoundsDebugPlugin
 * @classdesc Draw debug shapes in the scene for testing
 *
 * ***This is an optional jgg component, and so will be only be available on request when extending {@link AppBase}.***
 *
 * <pre>
 * components : {
 *  boundsDB : BoundsDebugPlugin
 * }
 * </pre>
 *
 * As this component extends [Phaser.Plugins.ScenePlugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.ScenePlugin.html} there is no way to pass a default configuration through.<br>
 * If you would like different default you should either extend this class or change them at runtime.
 *
 * once installed you can access from the scenes
 *
 * <pre>
 * scene.boundsDB
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Scene Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.installScenePlugin('key', BoundsDebugPlugin, 'scenekey);
 * </pre>
 *
 */
class BoundsDebugPlugin extends Phaser.Plugins.ScenePlugin {
  constructor(scene, pluginManager) {
    super(scene, pluginManager);
    this._graphicsDepth = 99;
    this._defaultColour = 0x00ff00;
    this._defaultLineWidth = 2;
  }

  init(config) {
    console.log('BoundsDebugPlugin', config);
  }

  boot() {
    this.scene.events.on('shutdown', this.shutdown, this);
  }

  /**
   * The default colour width of the rendered graphics
   * @param {number} [colour=0x00ff00] The Colour to use
   */
  setDefaultColour(colour) {
    this._defaultColour = colour || this._defaultColour;
  }

  /**
   * The default line width of the rendered graphics
   * @param {number} [width=2] The width to use
   */
  setDefaultLineWidth(width) {
    this._defaultLineWidth = width || this._defaultLineWidth;
  }

  /**
   * The default display depth or the rendered graphics
   * @param {number} [depth=99] The width to use
   */
  setDepth(depth) {
    this._graphicsDepth = depth;
    if (this._graphics) {
      this._graphics.setDepth(this._graphicsDepth);
    }
  }

  /**
   * Clear the current display
   */
  clearDisplay() {
    if (this._graphics) {
      this._graphics.clear();
    }
  }

  /**
   * Draw a debug rectangle
   * @param {Object} rect The area to draw {x,y, width,height}
   * @param {number} [colour=0x00ff00]
   * @param {*} [lineWidth=2]
   */
  drawBounds({ x, y, width, height }, colour, lineWidth) {
    this._display.lineStyle(lineWidth || this._defaultLineWidth, colour || this._defaultColour, 1);
    this._display.strokeRect(x, y, width, height);
  }

  /**
   * Draw a debug line
   * @param {number} sx The starting x value of the line
   * @param {number} sy The starting y value of the line
   * @param {number} ex The ending x value of the line
   * @param {number} ey The ending y value of the line
   * @param {number} [colour=0x00ff00] The colour of the line
   * @param {number} [lineWidth=2] The line width
   */
  drawLine(sx, sy, ex, ey, colour, lineWidth) {
    this._display.lineStyle(lineWidth || this._defaultLineWidth, colour || this._defaultColour, 1);
    this._display.lineBetween(sx, sy, ex, ey);
  }

  /**
   * Draw a debug circle
   * @param {number} x The y position of the circle
   * @param {number} y The x position of the circle
   * @param {number} radius The radius of the circle
   * @param {number} [colour=0x00ff00] The colour of the line
   * @param {number} [lineWidth=2] The line width
   */
  drawCircle(x, y, radius, colour, lineWidth) {
    this._display.lineStyle(lineWidth || this._defaultLineWidth, colour || this._defaultColour, 1);
    this._display.strokeCircle(x, y, radius);
  }

  /**
   * Draw a debug line connecting an array of points
   * @param {array} points An array of all the points to connect
   * @param {bool} [close=false] If should automatically close the path connecting last and first point
   * @param {number} [colour=0x00ff00] The colour of the line
   * @param {number} [lineWidth=2] The line width
   */
  drawPoints(points, close = false, colour, lineWidth) {
    this._display.lineStyle(lineWidth || this._defaultLineWidth, colour || this._defaultColour, 1);
    this._display.moveTo(points[0].x, points[0].y);
    points.forEach((p) => this._display.lineTo(p.x, p.y));
    if (close) this._display.lineTo(points[0].x, points[0].y);
    this._display.stroke();
  }

  /** @private */
  _createDisplay() {
    if (this._graphics) return;
    this._graphics = this.scene.add.graphics();
    this._graphics.setDepth(this._graphicsDepth);
  }

  /** @private */
  _destroyDisplay() {
    if (this._graphics) {
      this._graphics.destroy ? this._graphics.destroy() : false;
      this._graphics = null;
    }
  }

  /** @private */
  get _display() {
    if (!this._graphics) {
      this._createDisplay();
    }
    return this._graphics;
  }

  /** @private */
  shutdown() {
    this._destroyDisplay();
  }

  /** @private */
  destroy() {
    this.shutdown();
    super.destroy();
  }
}

export { BoundsDebugPlugin };
